// ============================================================================
// HelpInfoModal.js - Reusable Help Modal Component
// Location: frontend/src/components/HelpInfoModal.js
// ============================================================================
// Displays contextual help information in a modal dialog.
// ALL CAPS text is automatically rendered in bold.
// ============================================================================

import React from 'react';

/**
 * Formats content by making ALL CAPS text bold
 * Handles both line-starting headers and inline ALL CAPS phrases
 */
const formatContent = (content) => {
  if (!content) return null;
  
  // Split content into lines
  const lines = content.split('\n');
  
  return lines.map((line, lineIndex) => {
    // Check if the entire line (or start of line) is ALL CAPS (header style)
    // Match patterns like "CREATING TEE TIMES" or "PLAYER ROLES" at start of line
    const headerMatch = line.match(/^([A-Z][A-Z0-9\s&\-\(\)]+)(\s*[-–—]?\s*)?(.*)$/);
    
    if (headerMatch && headerMatch[1].length > 3 && headerMatch[1] === headerMatch[1].toUpperCase() && /[A-Z]{2,}/.test(headerMatch[1])) {
      // This line starts with an ALL CAPS header
      const headerText = headerMatch[1].trim();
      const separator = headerMatch[2] || '';
      const restOfLine = headerMatch[3] || '';
      
      return (
        <React.Fragment key={lineIndex}>
          <strong style={{ fontWeight: 700 }}>{headerText}</strong>
          {separator}
          {restOfLine && formatInlineAllCaps(restOfLine)}
          {lineIndex < lines.length - 1 && '\n'}
        </React.Fragment>
      );
    }
    
    // For lines that don't start with headers, check for inline ALL CAPS
    return (
      <React.Fragment key={lineIndex}>
        {formatInlineAllCaps(line)}
        {lineIndex < lines.length - 1 && '\n'}
      </React.Fragment>
    );
  });
};

/**
 * Formats inline ALL CAPS phrases within a line
 * Matches phrases like "SHORT URL", "IMPORTANT:", etc.
 */
const formatInlineAllCaps = (text) => {
  if (!text) return null;
  
  // Match ALL CAPS words/phrases (2+ chars, may include numbers and common punctuation)
  // But exclude single letters and common words that might be caps by accident
  const parts = text.split(/(\b[A-Z][A-Z0-9]{1,}(?:\s+[A-Z][A-Z0-9]{1,})*[:]?\b)/g);
  
  return parts.map((part, index) => {
    // Check if this part is an ALL CAPS phrase (at least 2 consecutive caps, not just "A" or "I")
    if (part && /^[A-Z][A-Z0-9]+(?:\s+[A-Z][A-Z0-9]+)*[:]?$/.test(part) && part.length >= 2) {
      return <strong key={index} style={{ fontWeight: 700 }}>{part}</strong>;
    }
    return part;
  });
};

const HelpInfoModal = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 20
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          maxWidth: 600,
          width: '100%',
          maxHeight: '80vh',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: 20,
            fontWeight: 700,
            color: '#1b4332'
          }}>
            {title || 'Help'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 24,
              cursor: 'pointer',
              color: '#6b7280',
              padding: '4px 8px',
              borderRadius: 4,
              lineHeight: 1
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{
          padding: 20,
          overflowY: 'auto',
          flex: 1
        }}>
          <div style={{
            fontSize: 15,
            lineHeight: 1.6,
            color: '#374151',
            whiteSpace: 'pre-line'
          }}>
            {formatContent(content)}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#2d6a4f',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpInfoModal;
